import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Sparkles, GraduationCap, ChevronLeft, Search } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { useSeo } from '../hooks/useSeo';
import { ENQUETES_INDEX, getPublishedEnquetes } from '../data/magazinesEnquetes';

/**
 * EnquetesListPage — Page listing publique des Enquêtes & Témoignages.
 *
 * URL canonique : /enquetes
 * SEO : JSON-LD ItemList + CollectionPage + BreadcrumbList
 * Audience : grand public (motards, parents, jeunes en formation).
 * Source : 27 études de cas CCS ECSR officielles adaptées en format magazine.
 */
export default function EnquetesListPage() {
  const published = useMemo(() => getPublishedEnquetes(), []);
  const comingSoon = useMemo(
    () => ENQUETES_INDEX.filter((e) => e.status !== 'published'),
    [],
  );

  // JSON-LD : ItemList des enquêtes publiées (SEO riches résultats Google).
  const itemListJsonLd = useMemo(() => ({
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Enquêtes & Témoignages Code 2 Roues',
    description:
      "Série d'enquêtes pilotes adaptées des 27 études de cas officielles CCS élaborées pour la formation ECSR. Format magazine grand public : accidentologie, témoignages, sources officielles (ONISR, MAIDS, Sécurité Routière).",
    url: 'https://code2roues.fr/enquetes',
    isPartOf: {
      '@type': 'WebSite',
      name: 'CODE 2 ROUES',
      url: 'https://code2roues.fr',
    },
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: published.length,
      itemListElement: published.map((e, idx) => ({
        '@type': 'ListItem',
        position: idx + 1,
        url: `https://code2roues.fr/enquete/${e.slug}`,
        name: e.title,
        description: e.teaser,
      })),
    },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://code2roues.fr/' },
        { '@type': 'ListItem', position: 2, name: 'Enquêtes', item: 'https://code2roues.fr/enquetes' },
      ],
    },
  }), [published]);

  useSeo({
    title: 'Enquêtes & Témoignages — 31 dossiers sécurité moto et 2-roues',
    description:
      "31 enquêtes pédagogiques pour comprendre les accidents réels en deux-roues. Adaptées des 27 études de cas officielles CCS ECSR + 4 dossiers premium (angle mort, trajectoire E-I-E, équipement 2026, pression pneus). Ados, livreurs, fatigue, alcool, 125, surestimation, risque perçu, nuit, vitesse, équipement, pneus, ville, signalisation AM, transmission, achat occasion, éclairage, physique 2 roues, freinage, virage. Données ONISR, MAIDS, témoignages réels. 100 % gratuit.",
    keywords:
      'enquêtes moto, témoignages 2-roues, accidentologie moto, ados mobylette, livreurs scoot, fatigue moto, alcool moto, 125 cm3, risque perçu, conduite nocturne, ONISR, CCS ECSR, physique moto, freinage moto, virage moto',
    path: '/enquetes',
    jsonLd: itemListJsonLd,
  });

  return (
    <div className="min-h-screen" data-testid="enquetes-list-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        {/* Breadcrumb */}
        <nav className="mb-4" aria-label="Fil d'Ariane">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-xs sm:text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="enquetes-breadcrumb-home"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Retour à l'accueil
          </Link>
        </nav>

        {/* HEADER */}
        <header className="mb-8 sm:mb-12">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-1.5 h-1.5 rounded-full bg-primary" />
            <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-primary">
              Série complète · 31 numéros publiés
            </span>
          </div>
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight max-w-3xl"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Enquêtes &amp; Témoignages
          </h1>
          <p className="mt-3 sm:mt-4 text-sm sm:text-base text-foreground/70 max-w-3xl leading-relaxed">
            Quatorze dossiers de fond pour comprendre <strong>ce qui tue réellement à deux-roues</strong>.
            Adaptés des <strong>27 études de cas officielles CCS ECSR</strong> en format magazine grand public :
            accidentologie chiffrée, témoignages réels, sources officielles (ONISR, MAIDS, Sécurité Routière).
            Pas de leur leçons — des faits, des données, des conseils vérifiables.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2 text-[11px] sm:text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border bg-card/40">
              <Sparkles className="w-3 h-3 text-amber-500" /> 100 % gratuit, sans inscription
            </span>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border bg-card/40">
              <GraduationCap className="w-3 h-3" /> Basé sur les CCS ECSR 2026
            </span>
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border bg-card/40">
              <Search className="w-3 h-3" /> Sources officielles citées
            </span>
          </div>
        </header>

        {/* GRID des enquêtes publiées */}
        <section aria-labelledby="enquetes-publiees">
          <h2 id="enquetes-publiees" className="sr-only">
            Enquêtes publiées
          </h2>
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5"
            data-testid="enquetes-grid"
          >
            {published.map((e) => (
              <EnqueteCard key={e.slug} entry={e} />
            ))}
          </div>
        </section>

        {/* Coming soon (si plusieurs slots ajoutés plus tard) */}
        {comingSoon.length > 0 && (
          <section className="mt-10" aria-labelledby="enquetes-a-venir">
            <h2
              id="enquetes-a-venir"
              className="text-base font-bold text-muted-foreground mb-3 uppercase tracking-wider text-[12px]"
            >
              Prochains numéros
            </h2>
            <ul className="flex flex-wrap gap-2">
              {comingSoon.map((e) => (
                <li
                  key={e.slug}
                  className="text-[11px] sm:text-xs px-3 py-1.5 rounded-full border border-dashed text-muted-foreground bg-muted/20"
                >
                  N°{e.number} · {e.short}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Maillage interne SEO : liens vers Magazine ETM + pages piliers */}
        <section
          className="mt-12 sm:mt-16 rounded-2xl border bg-card/40 p-5 sm:p-7"
          aria-labelledby="maillage-interne"
        >
          <h2
            id="maillage-interne"
            className="text-lg sm:text-xl font-extrabold tracking-tight mb-2"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Continuer ta préparation
          </h2>
          <p className="text-xs sm:text-sm text-muted-foreground mb-4 max-w-2xl">
            Les Enquêtes complètent ton apprentissage. Pour la préparation officielle
            à l'examen théorique moto (ETM), explore aussi :
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
            <Link
              to="/magazine"
              className="group rounded-lg border p-3 sm:p-4 hover:border-primary hover:shadow-sm transition-all bg-card"
              data-testid="link-magazine-etm"
            >
              <div className="text-[10px] uppercase tracking-wider font-bold text-primary mb-1">
                Magazine ETM
              </div>
              <div className="text-sm font-bold leading-tight">9 numéros pédagogiques</div>
              <div className="text-[11px] text-muted-foreground mt-1 leading-snug">
                9 dossiers ETM en format magazine éditorial
              </div>
            </Link>
            <Link
              to="/revision"
              className="group rounded-lg border p-3 sm:p-4 hover:border-primary hover:shadow-sm transition-all bg-card"
              data-testid="link-revision"
            >
              <div className="text-[10px] uppercase tracking-wider font-bold text-primary mb-1">
                Révision
              </div>
              <div className="text-sm font-bold leading-tight">9 thèmes officiels</div>
              <div className="text-[11px] text-muted-foreground mt-1 leading-snug">
                Mode examen + mode libre + erreurs
              </div>
            </Link>
            <Link
              to="/programme-etm"
              className="group rounded-lg border p-3 sm:p-4 hover:border-primary hover:shadow-sm transition-all bg-card"
              data-testid="link-programme-etm"
            >
              <div className="text-[10px] uppercase tracking-wider font-bold text-primary mb-1">
                Programme ETM
              </div>
              <div className="text-sm font-bold leading-tight">L’épreuve théorique moto</div>
              <div className="text-[11px] text-muted-foreground mt-1 leading-snug">
                40 questions, 9 thèmes, 35/40 requis
              </div>
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}

function EnqueteCard({ entry }) {
  const { slug, number, title, teaser, accent, issue } = entry;
  const hero = issue?.heroImage;
  const readMin = issue?.readTimeMin || 12;
  const category = issue?.category || 'Enquête';

  return (
    <Link
      to={`/enquete/${slug}`}
      className="group flex flex-col rounded-xl border bg-card overflow-hidden hover:border-primary hover:-translate-y-0.5 hover:shadow-md transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      data-testid={`enquete-card-${slug}`}
      aria-label={`Lire l'enquête n°${number} : ${title}`}
    >
      {hero && (
        <div
          className="relative w-full aspect-[16/9] overflow-hidden"
          style={{ background: 'hsl(var(--muted))' }}
        >
          <img
            src={hero}
            alt={issue?.heroImageAlt || title}
            loading="lazy"
            width={640}
            height={360}
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
          />
          <div
            className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent pointer-events-none"
            aria-hidden="true"
          />
          <Badge
            className="absolute top-2.5 left-2.5 text-[10px] border-none"
            style={{ backgroundColor: accent || '#cf2031', color: '#fff' }}
          >
            Enquête n°{number}
          </Badge>
        </div>
      )}
      <div className="flex-1 flex flex-col p-4 sm:p-5">
        <div className="text-[10px] uppercase tracking-wider font-bold text-muted-foreground mb-1">
          {category}
        </div>
        <h3
          className="text-base sm:text-lg font-extrabold leading-tight tracking-tight mb-2"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          {title}
        </h3>
        <p className="text-xs sm:text-sm text-foreground/70 leading-relaxed line-clamp-3 flex-1">
          {teaser}
        </p>
        <div className="mt-3 pt-3 border-t flex items-center justify-between text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Clock className="w-3 h-3" /> {readMin} min
          </span>
          <span className="inline-flex items-center gap-1 font-bold text-primary group-hover:translate-x-0.5 transition-transform">
            Lire <ArrowRight className="w-3.5 h-3.5" />
          </span>
        </div>
      </div>
    </Link>
  );
}
